import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  DestroyRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';

import { UtilizadorService } from '@core/services/domain-services';
import { Utilizador } from '@core/models/models';
import { FeedbackService } from '@core/services/feedback.service';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';

const MAX_FOTO_SIZE = 5 * 1024 * 1024;
const ALLOWED_FOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

@Component({
  selector: 'app-editar-utilizador',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DrawerModule,
    ButtonModule,
    InputTextModule,
    Select,
  ],
  templateUrl: './editar-utilizador.component.html',
  styleUrl: './editar-utilizador.component.scss',
})
export class EditarUtilizadorComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() utilizador: Utilizador | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  userForm!: FormGroup;
  loading = false;
  submitted = false;

  isCliente = false;

  perfilOptions = [
    { label: 'Efetivo', value: 'EFETIVO' },
    { label: 'Cliente', value: 'CLIENTE' },
    { label: 'Admin', value: 'ADMIN' },
  ];

  /** Foto nova escolhida pelo utilizador (se null, mantém a existente). */
  selectedFoto: File | null = null;
  /** Preview: URL.createObjectURL (foto nova) ou o URL vindo da API (foto existente). */
  fotoPreviewUrl: string | null = null;
  private objectUrlToRevoke: string | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly feedback = inject(FeedbackService);
  private readonly userSvc = inject(UtilizadorService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.initForm();
  }

  private initForm(): void {
    this.userForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(3)]],
      nome: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      tipoPerfil: ['EFETIVO'],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['visible'] || changes['utilizador']) &&
      this.visible &&
      this.utilizador
    ) {
      this.resetState();
      const rawPerfil = (this.utilizador.tipoPerfil || 'EFETIVO').toUpperCase();
      this.isCliente = rawPerfil === 'CLIENTE';

      this.userForm.patchValue({
        userName: this.utilizador.userName,
        nome: this.utilizador.nome,
        email: this.utilizador.email,
        tipoPerfil: rawPerfil,
      });

      if (this.isCliente) {
        this.userForm.get('tipoPerfil')?.disable();
      } else {
        this.userForm.get('tipoPerfil')?.enable();
      }

      this.fotoPreviewUrl = this.utilizador.fotografia || null;
    }
  }

  ngOnDestroy(): void {
    this.revokeObjectUrl();
  }

  private resetState(): void {
    this.userForm.reset();
    this.isCliente = false;
    this.userForm.get('tipoPerfil')?.enable();
    this.selectedFoto = null;
    this.revokeObjectUrl();
    this.fotoPreviewUrl = null;
    this.submitted = false;
  }

  get f() {
    return this.userForm.controls;
  }

  async onFotoSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];

    if (file.size > MAX_FOTO_SIZE) {
      this.feedback.showError('Fotografia muito grande. Tamanho máximo: 5MB.', {
        summary: 'Ficheiro Inválido',
        life: 4000,
      });
      input.value = '';
      return;
    }
    if (!ALLOWED_FOTO_TYPES.includes(file.type)) {
      this.feedback.showError('Formato inválido. Use JPG, PNG ou WEBP.', {
        summary: 'Formato Não Suportado',
        life: 4000,
      });
      input.value = '';
      return;
    }

    const compressedFile = await compressImageIfNeeded(file);
    this.selectedFoto = compressedFile;
    this.revokeObjectUrl();
    this.fotoPreviewUrl = this.objectUrlToRevoke = URL.createObjectURL(compressedFile);
  }

  removeFoto(): void {
    this.selectedFoto = null;
    this.revokeObjectUrl();
    this.fotoPreviewUrl = this.utilizador?.fotografia || null;
  }

  private revokeObjectUrl(): void {
    if (this.objectUrlToRevoke) {
      URL.revokeObjectURL(this.objectUrlToRevoke);
      this.objectUrlToRevoke = null;
    }
  }

  closeDrawer(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetState();
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.userForm.invalid || !this.utilizador?.id) {
      this.feedback.showWarning('Por favor, preencha os campos obrigatórios.', {
        summary: 'Formulário Incompleto',
        life: 4000,
      });
      return;
    }

    this.loading = true;
    const formValues = this.userForm.getRawValue();

    const formData = new FormData();
    formData.append('Nome', formValues.nome?.trim() ?? '');
    formData.append('Email', formValues.email?.trim() ?? '');
    formData.append('UserName', formValues.userName?.trim() ?? '');
    if (formValues.tipoPerfil) {
      formData.append('TipoPerfilEnum', formValues.tipoPerfil);
    }
    if (this.selectedFoto) {
      formData.append('Fotografia', this.selectedFoto, this.selectedFoto.name);
    }

    this.userSvc
      .atualizarConta(this.utilizador.id, formData)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          const detail = this.feedback.extractSuccessMessage(
            res,
            'Os dados do utilizador foram atualizados com sucesso.',
          );
          this.feedback.showSuccess(detail, {
            summary: 'Utilizador Atualizado',
            life: 4000,
          });
          this.saved.emit();
          setTimeout(() => this.closeDrawer(), 800);
        },
        error: () => {
          // O interceptor global já apresenta a mensagem de erro do backend com precisão
        },
      });
  }
}
